import { apiSlice } from "../stores/base/api-slice";
import { BulkPurchaseTransaction, BulkPurchaseListResponse, BulkPurchaseSearchParams } from "../model/bulk-purchase/bulk-purchase";
import {
	BulkPurchaseCardResult,
	BulkPurchaseItemRequest,
	CustomerBulkPurchaseSearchParams,
	UploadBulkPurchaseCardRequest,
	UploadBulkPurchaseWalletRequest,
	ValidateBulkPurchaseResponse,
} from "../model/bulk-purchase/bulk-purchase-customer";

const bulkPurchaseEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getBulkPurchases: builder.query<BulkPurchaseListResponse, BulkPurchaseSearchParams>({
			query: (params) => ({
				url: "/transactions/admin/bulk-purchase",
				method: "GET",
				params,
			}),
			providesTags: ["BulkPurchases"],
		}),
		getUtilityAdminBulkPurchases: builder.query<BulkPurchaseListResponse, BulkPurchaseSearchParams>({
			query: (params) => ({
				url: "/transactions/utility-admin/bulk-purchase",
				method: "GET",
				params,
			}),
			providesTags: ["BulkPurchases"],
		}),
		getCustomerBulkPurchases: builder.query<BulkPurchaseListResponse, CustomerBulkPurchaseSearchParams>({
			query: (params) => ({
				url: "/transactions/customer/bulk-purchase",
				method: "GET",
				params,
			}),
			providesTags: ["BulkPurchases"],
		}),
		getBulkPurchaseById: builder.query<BulkPurchaseTransaction, string>({
			query: (transactionReference) => ({
				url: `/transactions/bulk-purchase-transaction/${transactionReference}`,
				method: "GET",
			}),
			providesTags: ["BulkPurchases"],
		}),
		downloadBulkPurchaseInvoice: builder.query<Blob, string>({
			query: (transactionReference) => ({
				url: `/pdf/download-bulk-purchase-invoice/${transactionReference}`,
				method: "GET",
				responseHandler: (response: Response) => {
					return response.blob();
				},
			}),
		}),

		// --- Customer bulk buy-electricity authoring flow (mirrors mobile) ---

		// Validate manually-entered line items. Body is a JSON array of items.
		validateManualBulkPurchase: builder.mutation<ValidateBulkPurchaseResponse, BulkPurchaseItemRequest[]>({
			query: (items) => ({
				url: "/vending/bulk/purchase/validate/manual",
				method: "POST",
				body: items,
			}),
		}),

		// Upload the filled .xlsx template; the backend parses + validates it and returns rows.
		validateXlsxBulkPurchase: builder.mutation<ValidateBulkPurchaseResponse, File>({
			query: (file) => {
				const formData = new FormData();
				formData.append("file", file);
				return {
					url: "/vending/bulk/purchase/validate/xlsx",
					method: "POST",
					body: formData,
				};
			},
		}),

		// Submit + pay from wallet. Response body is the bare transaction reference string.
		uploadBulkPurchaseWallet: builder.mutation<string, UploadBulkPurchaseWalletRequest>({
			query: (body) => ({
				url: "/vending/bulk/purchase/upload/wallet",
				method: "POST",
				body,
				responseHandler: async (response: Response) => {
					const text = await response.text();
					// backend returns a bare JSON string (or plain string) reference
					try {
						return JSON.parse(text);
					} catch {
						return text;
					}
				},
			}),
			invalidatesTags: ["BulkPurchases", "WalletBalance"],
		}),

		// Submit + pay by card via Payment Hub. Response carries the redirect returnUrl + reference.
		uploadBulkPurchaseCard: builder.mutation<BulkPurchaseCardResult, UploadBulkPurchaseCardRequest>({
			query: (body) => ({
				url: "/vending/bulk/purchase/upload/card",
				method: "POST",
				body,
			}),
			invalidatesTags: ["BulkPurchases"],
		}),

		// Download the blank XLSX template and trigger a browser save.
		downloadBulkPurchaseTemplate: builder.query<void, void>({
			query: () => ({
				url: "/vending/bulk/purchase/template/xlsx",
				method: "GET",
				cache: "no-cache",
				responseHandler: async (response: Response) => {
					const blob = await response.blob();
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = "BulkPurchaseTemplate.xlsx";
					a.click();
					window.URL.revokeObjectURL(url);
				},
			}),
		}),
	}),
});

export const {
	useGetBulkPurchasesQuery,
	useGetUtilityAdminBulkPurchasesQuery,
	useGetCustomerBulkPurchasesQuery,
	useGetBulkPurchaseByIdQuery,
	useLazyDownloadBulkPurchaseInvoiceQuery,
	useValidateManualBulkPurchaseMutation,
	useValidateXlsxBulkPurchaseMutation,
	useUploadBulkPurchaseWalletMutation,
	useUploadBulkPurchaseCardMutation,
	useLazyDownloadBulkPurchaseTemplateQuery,
} = bulkPurchaseEndpoints;
